import { useTranslation } from "react-i18next";
import { outsideExpected } from "../expected";
import type { PointState } from "../generated/widget-fixture";
import { key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/** The trend line's path in a 100 × 24 box: the series scaled to its own minimum and maximum. */
function trendPath(points: [string, number | null][]): string | undefined {
  const values = points.flatMap(([at, v]) => (typeof v === "number" ? [[Date.parse(at), v] as const] : []));
  const firstAt = values[0]?.[0];
  const lastAt = values.at(-1)?.[0];
  if (values.length < 2 || firstAt === undefined || lastAt === undefined) return undefined;
  const t0 = firstAt;
  const span = lastAt - t0 || 1;
  const lo = Math.min(...values.map(([, v]) => v));
  const hi = Math.max(...values.map(([, v]) => v));
  const range = hi - lo || 1;
  return values
    .map(
      ([t, v], i) =>
        `${i ? "L" : "M"}${(((t - t0) / span) * 100).toFixed(1)} ${(22 - ((v - lo) / range) * 20).toFixed(1)}`,
    )
    .join("");
}

/**
 * device_tile: one device in a small card for the home page (VisionPlatform device-type-pages DTP-11). The device's
 * name and online dot, then its 1 to 4 values in a dense two-column block, amber outside the normal band (DDB-03),
 * and with `trend` a thin line of the first value's history. No title, axes or legend.
 */
export function DeviceTile({ widget, input, now, onOpenDevice }: WidgetProps) {
  const fmt = useWidgetText();
  const { t, i18n } = useTranslation();
  const bindings = (widget.bindings ?? []).filter((b) => b.datapoint);
  const deviceId = bindings[0]?.device ?? "";
  const device = input.devices?.[deviceId];
  const name = widget.title ?? device?.name ?? "";
  const cells = bindings.map((b) => ({ b, p: input.points?.[key(b.device, b.datapoint ?? "")] }));
  const text = (p: PointState | undefined) => {
    const v = p?.latest?.value;
    if (!p || v === undefined || v === null) return fmt.t("state.noData");
    if (typeof v === "number") return fmt.value(v, p);
    if (typeof v === "boolean") return fmt.t(v ? "toggle.on" : "toggle.off");
    if (typeof v === "string") return p.labels?.[v] ?? v;
    return "";
  };
  const any = cells.some((c) => c.p?.latest);
  const offline = device?.state === "offline";
  const state = offline ? "offline" : any ? "normal" : "empty";
  const first = bindings[0];
  const series = first?.datapoint ? input.history?.[key(first.device, first.datapoint)] : undefined;
  const line =
    widget.config?.trend === true && series ? trendPath(series.points as [string, number | null][]) : undefined;
  const stateText = device ? fmt.deviceState(device.state) : "";
  const values = cells.filter((c) => c.p?.latest).map((c) => `${c.p?.name ?? ""} ${text(c.p)}`);
  const label = `${name}${stateText ? `, ${stateText.toLocaleLowerCase(i18n.language)}` : ""}${
    values.length ? `: ${values.join(", ")}` : ""
  }`;
  const seen = device?.last_seen_at;
  const body = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${
            device?.state === "online" ? "bg-emerald-600" : device?.state === "sleeping" ? "bg-sky-600" : "bg-gray-400"
          }`}
        />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{name}</h3>
        {stateText && <span className="shrink-0 text-xs text-gray-700">{stateText}</span>}
      </div>
      {any ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
          {cells.map(({ b, p }) => {
            const v = p?.latest?.value;
            const outside = outsideExpected(p, v);
            return (
              <div
                key={key(b.device, b.datapoint ?? "")}
                className="min-w-0"
                {...(outside ? { "data-expected": "outside" } : {})}
              >
                <dt className="truncate text-xs text-gray-700">{b.label ?? p?.name}</dt>
                <dd
                  className={`truncate text-base leading-tight font-semibold tabular-nums ${
                    outside ? "text-amber-800" : offline ? "text-gray-500" : "text-gray-900"
                  }`}
                >
                  {text(p)}
                  {outside && <span className="sr-only">, {t("widgets.outsideExpected")}</span>}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
        <>
          <ul className="flex flex-wrap gap-x-2 text-xs text-gray-700">
            {cells.map(({ b, p }) => (
              <li key={key(b.device, b.datapoint ?? "")}>{b.label ?? p?.name}</li>
            ))}
          </ul>
          <p className="text-sm text-gray-700">{fmt.t("state.noData")}</p>
        </>
      )}
      {line && !offline && (
        <svg
          aria-hidden="true"
          viewBox="0 0 100 24"
          preserveAspectRatio="none"
          className="mt-auto h-5 w-full text-emerald-700"
        >
          <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
      {offline && seen && <p className="mt-auto text-xs text-amber-900">{fmt.age(seen, now)}</p>}
    </>
  );
  // Tapping the tile opens the device page (DTP-11), when the page can: one button over the whole card.
  return (
    <section
      data-widget={widget.type}
      data-widget-state={state}
      aria-label={label}
      className="@container relative flex h-full flex-col gap-1.5 rounded-xl border border-gray-200 bg-white p-3 text-sm shadow-xs"
    >
      {body}
      {onOpenDevice && (
        <button
          type="button"
          aria-label={t("widgets.openDevice", { name })}
          onClick={() => onOpenDevice(deviceId)}
          className="absolute inset-0 rounded-xl hover:bg-gray-900/[0.03] focus-visible:outline-2 focus-visible:outline-brand"
        />
      )}
    </section>
  );
}
